/**
 * Timezone-free calendar dates and proleptic Gregorian day arithmetic. The one
 * place in `packages/domain` where day arithmetic is implemented.
 *
 * Planning dates in ComPlan are civil dates, not instants: an Initiative
 * that starts on 4 January starts on 4 January regardless of where the browser
 * sits (design.md, technology decisions: "planning dates themselves are
 * timezone-free"). A JavaScript `Date` is an instant, so using one here would
 * make the ISO week grid depend on the host offset. This module therefore works
 * on `{ year, month, day }` triples and on epoch day numbers, with no `Date`
 * and no environment access at all - `packages/domain` is pure.
 *
 * The civil-to-days conversion is the standard shifted-era algorithm: the epoch
 * is moved to 0000-03-01 so that leap days land at the end of the internal year
 * and the 400-year cycle of 146,097 days divides evenly. It is exact for every
 * year in the supported range and needs no lookup tables.
 *
 * Everything that needs to move a date by a day - the ISO week grid, bar
 * geometry, the Sales_Cycle gap boundaries - goes through {@link addDays} here.
 * See `./plain-date.js` for the `YYYY-MM-DD` boundary type, which converts into
 * this representation rather than reimplementing any of it.
 */
/** Lowest year the calendar helpers accept. */
export const MIN_CALENDAR_YEAR = 1;
/** Highest year the calendar helpers accept. */
export const MAX_CALENDAR_YEAR = 9999;
/** Days in a week. Named because the grid's seven-day columns depend on it. */
export const DAYS_PER_WEEK = 7;
/** Gregorian leap year rule. */
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
/**
 * Length of `month` in `year`. Throws for a month outside 1-12.
 *
 * Throwing rather than returning zero is deliberate: a month number outside the
 * calendar is a defect in the caller, and a zero length would flow on as "no
 * such day exists" and be indistinguishable from a legitimate answer. Callers
 * that must tolerate arbitrary input test the month first - see
 * {@link isValidCalendarDate}, which is the only guard the package needs.
 */
export function daysInMonth(year, month) {
    switch (month) {
        case 1:
        case 3:
        case 5:
        case 7:
        case 8:
        case 10:
        case 12:
            return 31;
        case 4:
        case 6:
        case 9:
        case 11:
            return 30;
        case 2:
            return isLeapYear(year) ? 29 : 28;
        default:
            throw new RangeError(`month must be 1-12, received ${String(month)}`);
    }
}
/** Whether the triple names a real day. */
export function isValidCalendarDate(candidate) {
    const { year, month, day } = candidate;
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day))
        return false;
    if (month < 1 || month > 12)
        return false;
    return day >= 1 && day <= daysInMonth(year, month);
}
/** Throws when the triple is not a real day; returns it otherwise. */
export function assertCalendarDate(candidate) {
    if (!isValidCalendarDate(candidate)) {
        throw new RangeError(`not a calendar date: ${String(candidate.year)}-${String(candidate.month)}-${String(candidate.day)}`);
    }
    return candidate;
}
/** Constructs a validated {@link CalendarDate}. */
export function calendarDate(year, month, day) {
    return assertCalendarDate({ year, month, day });
}
/**
 * Civil date to epoch day.
 *
 * Shifts the year to start in March (`era` arithmetic) so the 400-year cycle is
 * a flat 146,097 days and February's variable length falls at the year's end.
 */
export function toEpochDay(date) {
    const { year, month, day } = assertCalendarDate(date);
    const shiftedYear = month <= 2 ? year - 1 : year;
    const era = Math.floor(shiftedYear / 400);
    const yearOfEra = shiftedYear - era * 400; // 0-399
    const marchBasedMonth = month > 2 ? month - 3 : month + 9; // 0-11, March = 0
    const dayOfYear = Math.floor((153 * marchBasedMonth + 2) / 5) + day - 1; // 0-365
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146097 + dayOfEra - 719468;
}
/** Epoch day to civil date. Exact inverse of {@link toEpochDay}. */
export function fromEpochDay(epochDay) {
    if (!Number.isInteger(epochDay)) {
        throw new RangeError(`epoch day must be an integer, received ${String(epochDay)}`);
    }
    const shifted = epochDay + 719468;
    const era = Math.floor(shifted / 146097);
    const dayOfEra = shifted - era * 146097; // 0-146096
    const yearOfEra = Math.floor((dayOfEra -
        Math.floor(dayOfEra / 1460) +
        Math.floor(dayOfEra / 36524) -
        Math.floor(dayOfEra / 146096)) /
        365); // 0-399
    const shiftedYear = yearOfEra + era * 400;
    const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100)); // 0-365
    const marchBasedMonth = Math.floor((5 * dayOfYear + 2) / 153); // 0-11, March = 0
    const day = dayOfYear - Math.floor((153 * marchBasedMonth + 2) / 5) + 1;
    const month = marchBasedMonth < 10 ? marchBasedMonth + 3 : marchBasedMonth - 9;
    return { year: month <= 2 ? shiftedYear + 1 : shiftedYear, month, day };
}
/** `date` shifted by `days`, which may be negative. */
export function addDays(date, days) {
    if (!Number.isInteger(days)) {
        throw new RangeError(`days must be an integer, received ${String(days)}`);
    }
    return fromEpochDay(toEpochDay(date) + days);
}
/** Whole days from `from` to `to`; negative when `to` precedes `from`. */
export function daysBetween(from, to) {
    return toEpochDay(to) - toEpochDay(from);
}
/** Negative, zero or positive as `a` precedes, equals or follows `b`. */
export function compareCalendarDates(a, b) {
    return toEpochDay(a) - toEpochDay(b);
}
/** Whether both triples name the same day. */
export function calendarDatesEqual(a, b) {
    return a.year === b.year && a.month === b.month && a.day === b.day;
}
/**
 * `YYYY-MM-DD`. Used for diagnostics and stable test expectations, not for
 * display.
 *
 * The one zero-padding site in the package: `./plain-date.js` builds its branded
 * boundary values from this rather than padding a second time.
 */
export function toIsoDateString(date) {
    const { year, month, day } = assertCalendarDate(date);
    const pad = (value, width) => String(value).padStart(width, '0');
    return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}
/** First day of `year`. */
export function firstDayOfYear(year) {
    return calendarDate(year, 1, 1);
}
/** Last day of `year`. */
export function lastDayOfYear(year) {
    return calendarDate(year, 12, 31);
}
/** Whether `date` falls inside the calendar year `year`. */
export function isInCalendarYear(date, year) {
    return date.year === year;
}
//# sourceMappingURL=calendar-date.js.map